import { ComboboxEditor, Config, DataGrid, ErrorHandling, TranslationConfig } from "@serenity-is/corelib";
import { gridDefaults } from "@serenity-is/sleekgrid";
import flatpickr from "flatpickr";
import "flatpickr/dist/l10n";
import { getLanguageList } from "./Helpers/LanguageList";
import { installColumnPickerFilter } from "./UI/ColumnPickerFilter";
import { installFormValidation } from "./UI/FormValidation";
import { installCardReorder } from "./UI/CardReorder";
import { installCardView } from "./UI/CardView";
import { installListPageChrome } from "./UI/ListPageChrome";
import { installNavExpand } from "./UI/NavExpand";
import { installPasswordReveal } from "./UI/PasswordReveal";
import { installGridStates } from "./UI/GridStates";
import { installStackedDialogs } from "./UI/StackedDialogs";
import DOMPurify from "dompurify";

Config.rootNamespaces.push('BWDesignSystem');
TranslationConfig.getLanguageList = getLanguageList;
gridDefaults.sanitizer = (globalThis.DOMPurify = DOMPurify).sanitize;

/**
 * The two design generations specify different grid densities: v1 is a 32px dense
 * table (its whole point is 20 visible rows), v2 a 44px roomy one under a 38px head.
 *
 * This cannot be done in theme-core.css. SleekGrid writes the height it is going to use
 * into --sg-row-height as an INLINE custom property on the grid container, and uses
 * the same number to compute scroll offsets and the virtual viewport — so a
 * stylesheet rule is both outranked by the inline value and, if it did win, would
 * move the paint without moving the maths and rows would overlap.
 *
 * ⚠ The knob is gridDefaults.rowHeight, NOT DataGrid.defaultRowHeight. The latter
 * is what Serenity.Extensions' common-theme.js sets (to 36) and it does not take in
 * 10.3.6 — DataGrid.defaultOptions.rowHeight still reads null afterwards and grids
 * render at SleekGrid's own 30px default. gridDefaults is the object that is
 * actually read, and mutating it here provably reaches the live grid: the sanitizer
 * assigned on the line above lands on the same object.
 */
const themeVersion = document.documentElement?.getAttribute('data-theme-version') === 'v2' ? 'v2' : 'v1';
gridDefaults.rowHeight = themeVersion === 'v2' ? 44 : 32;

/**
 * Create and edit must open as a DIALOG in both design generations — v1 draws a
 * centred modal, v2 a right-hand drawer, and theme-core.css expresses each from the same
 * .modal markup.
 *
 * Left alone, Serenity 10.3.6 opens an editor from a route-handling grid as a
 * full-page PANEL instead: it replaces the list entirely, has no backdrop and no
 * modal chrome, so neither design's treatment applies and the screen falls back to
 * stock Serene. Pinning the option to false rather than leaving it null is what puts
 * the editor back in a dialog for both versions.
 */
DataGrid.defaultOptions.openDialogsAsPanel = false;

/**
 * The same decision for a LOOKUP's inplace-add, which is a separate code path with a
 * separate switch and no static default to pin.
 *
 * ⚠ `openDialogAsPanel` (singular) is a public INSTANCE property on ComboboxEditor, not an
 * entry in a defaultOptions object, and it starts life `undefined` — which corelib treats as
 * "panel". So the + on any lookup carrying [LookupEditor(InplaceAdd = true)] opened its create
 * dialog as a full-page `.s-Panel`: no scrim, no modal chrome, neither generation's dialog
 * treatment, and StackedDialogs.ts could not count it because it counts `.modal` elements.
 * Setting it on the PROTOTYPE is what makes that apply to every lookup in the application
 * rather than to whichever ones a page remembered to configure.
 *
 * ⚠ Assigned as a prototype default, so an individual editor can still override it on its own
 * instance — a prototype property is shadowed by an instance one, not the other way round.
 */
ComboboxEditor.prototype.openDialogAsPanel = false;

/**
 * Rearranges every list page into the list screen both handoffs draw — page header with
 * a record count, a tools row carrying search / a Filters toggle with a count / removable
 * filter chips / a Compact-Comfortable switch, and a collapsible filter panel. See
 * Modules/Common/UI/ListPageChrome.ts for why this hooks a static event rather than
 * touching each grid.
 */
installListPageChrome();

/**
 * The Cards / List switch beside the density switch, and the card presentation behind it. The
 * grid stays the data engine — search, filters, sort, paging and permissions are untouched; only
 * the drawing changes. See Modules/Common/UI/CardView.ts.
 *
 * ⚠ MUST be installed after installListPageChrome(): it appends its control to the .m-grid-tools
 * row that module creates, and DataGrid.onAfterInit notifies subscribers in subscription order.
 * Moved above it, the tools row does not exist yet and the switch silently never appears.
 */
installCardView();

// Drag-to-reorder for the dashboard's launch tiles. Cheap no-op everywhere else — it looks for the
// tile container and returns immediately if the page has none.
installCardReorder();

/**
 * The empty-grid and loading-skeleton states from handoff section 07. Separate from the
 * chrome above because these are reactive — they follow the view's load events and the
 * current filters — rather than a one-shot rearrangement at construction.
 */
installGridStates();

/**
 * Handoff section 06's level breadcrumb for stacked dialogs. Watches the document rather
 * than subclassing a dialog, because Serenity builds dialogs from several places that
 * share no base we control.
 */
installStackedDialogs();

/**
 * The v1 rail's second state: pinned open at 236px with each group's children listed
 * underneath it, instead of the 56px icon rail with hover flyouts. Driven from the top-bar
 * toggler, which at desktop widths had no behaviour at all. See Modules/Common/UI/NavExpand.ts
 * and Common/Theming/NavExpanded.cs — the server stamps the state so the rail is never briefly
 * the wrong width.
 */
installNavExpand();

/**
 * Keeps the selection checkbox, the row-expand chevron and the row-actions cell out of the
 * Column Picker — they are plumbing, not data, and unticking them breaks the grid. See
 * Modules/Common/UI/ColumnPickerFilter.ts for the one SleekGrid subtlety it has to handle.
 */
installColumnPickerFilter();

/**
 * The show/hide eye inside every password field — sign-in, sign-up, the user dialog and the
 * change/reset password screens Serenity.Extensions ships. A DOM watcher rather than an editor
 * subclass, because the button has to be the input's sibling and `PasswordEditor` offers no
 * post-attach hook. See Modules/Common/UI/PasswordReveal.ts.
 */
installPasswordReveal();

/**
 * Form-level validation says its piece ONCE, in the prototype's banner at the top of the
 * form, instead of three times (inline message + toast + tooltip). See
 * Modules/Common/UI/FormValidation.ts for exactly which of Serenity's four invalid-form
 * behaviours are kept and which are dropped.
 */
installFormValidation();

/*
 * Design generation — the profile menu's Theme 1 | Theme 2 control.
 *
 * A switcher lived here until 2026-08-18, when it was removed so the generation was a
 * deployment decision only. Restored 2026-08-20 as a per-browser choice beside Light/Dark.
 *
 * ⚠ It RELOADS rather than re-stamping `data-theme-version` on <html>, which is the whole
 * reason this is not two lines. Almost the entire theme would follow the attribute live, but
 * two things would not — grid row height is baked into each grid at construction
 * (gridDefaults.rowHeight above, because SleekGrid caches it for its scroll maths), and
 * Serenity builds dialogs against whichever mode was current when they were created. A
 * half-switched app, Theme 1 row heights under Theme 2 chrome, is worse than a reload.
 *
 * ⚠ A cookie, not localStorage: the server stamps the attribute while it renders, so the value
 * has to be readable synchronously at that moment. Common/Theming/ThemeVersion.cs reads this
 * cookie ahead of AppSettings:ThemeVersion.
 *
 * ⚠ The stored values stay `v1`/`v2` — "Theme 1"/"Theme 2" are display names only. Renaming the
 * attribute would mean rewriting every `[data-theme-version="vN"]` selector in the stylesheet.
 */
const THEME_VERSIONS = ['v1', 'v2'] as const;
type ThemeVersionValue = typeof THEME_VERSIONS[number];

document.addEventListener('click', e => {
    const btn = (e.target as HTMLElement)?.closest?.('[data-theme-version-set]') as HTMLElement;
    if (!btn)
        return;

    const wanted = btn.getAttribute('data-theme-version-set') as ThemeVersionValue;
    if (!THEME_VERSIONS.includes(wanted))
        return;

    // Already current — reloading would cost a page load and change nothing, which reads as a
    // bug rather than as a no-op.
    if (document.documentElement?.getAttribute('data-theme-version') === wanted)
        return;

    document.cookie = `BWDesignSystemThemeVersion=${wanted};path=/;max-age=31536000;samesite=lax`;
    location.reload();
});

/**
 * Light / Dark / Auto — the prototype's Theme control in the profile menu, plus the round
 * cycle button on the sign-in screen.
 *
 * ⚠ Unlike the Design switch above, this does NOT reload, and that difference is the point:
 * dark mode is nothing but which token block matches, so re-stamping `data-theme` on <html>
 * completes the switch in place. Nothing is baked into a widget at construction the way a
 * grid's row height is, so there is nothing left half-switched.
 *
 * The cookie is still what makes it survive navigation, and it is a cookie rather than
 * localStorage because the server stamps the attribute during render — see ThemeMode.cs.
 * `auto` is resolved here the same way the inline head script resolves it on load.
 */
const THEME_MODES = ['light', 'dark', 'auto'] as const;
type ThemeMode = typeof THEME_MODES[number];

function applyThemeMode(mode: ThemeMode) {
    const root = document.documentElement;
    const dark = mode === 'dark' ||
        (mode === 'auto' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);

    root.setAttribute('data-theme-mode', mode);
    root.setAttribute('data-theme', dark ? 'dark' : 'light');
    document.cookie = `BWDesignSystemThemeMode=${mode};path=/;max-age=31536000;samesite=lax`;

    // The control reflects the CHOICE, not the resolved value: with Auto selected the Auto
    // segment stays lit even though the page is rendering dark.
    document.querySelectorAll<HTMLElement>('[data-theme-mode-set]').forEach(b =>
        b.classList.toggle('is-on', b.getAttribute('data-theme-mode-set') === mode));

    const hint = document.querySelector('.m-profile-theme-hint');
    if (hint)
        hint.textContent = mode === 'auto'
            ? `Following your system setting — currently ${dark ? 'dark' : 'light'}.`
            : `Fixed ${mode} theme on this device.`;
}

document.addEventListener('click', e => {
    const target = e.target as HTMLElement;

    const btn = target?.closest?.('[data-theme-mode-set]') as HTMLElement;
    if (btn) {
        const wanted = btn.getAttribute('data-theme-mode-set') as ThemeMode;
        if (THEME_MODES.includes(wanted)) applyThemeMode(wanted);
        return;
    }

    // The sign-in screen has no profile menu, so the prototype gives it a single round
    // button that cycles light → dark → auto. One control, three states, no menu.
    const cycler = target?.closest?.('[data-theme-mode-cycle]') as HTMLElement;
    if (cycler) {
        const current = (document.documentElement.getAttribute('data-theme-mode') || 'auto') as ThemeMode;
        const next = THEME_MODES[(THEME_MODES.indexOf(current) + 1) % THEME_MODES.length];
        applyThemeMode(next);
        cycler.setAttribute('title', `Theme: ${next} (click to change)`);
    }
});

/**
 * "/" focuses the navigation search in the top bar, which is what the "/" chip
 * drawn next to it in the v1 handoff promises. Ignored while the caret is already
 * in a field, so typing a slash into a form still types a slash.
 */
document.addEventListener('keydown', e => {
    if (e.key !== '/' || e.ctrlKey || e.altKey || e.metaKey)
        return;

    const active = document.activeElement as HTMLElement;
    if (active && (/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) || active.isContentEditable))
        return;

    const search = document.querySelector('.s-sidebar-search-input') as HTMLInputElement;
    if (search) {
        e.preventDefault();
        search.focus();
        search.select();
    }
});

let culture = (document.documentElement?.lang || 'en').toLowerCase();
if (flatpickr.l10ns[culture]) {
    flatpickr.localize(flatpickr.l10ns[culture]);
} else {
    culture = culture.split('-')[0];
    flatpickr.l10ns[culture] && flatpickr.localize(flatpickr.l10ns[culture]);
}

window.onerror = ErrorHandling.runtimeErrorHandler;

/**
 * Serenity's rejection handler, with aborted requests filtered out first.
 *
 * ⚠ An AbortError is NOT a fault and must never reach the user. corelib aborts a fetch whenever a
 * newer one supersedes it, which is ordinary behaviour: a grid re-filtered before its first load
 * returned, a quick search typed faster than the round trip, a detail grid repointed at another
 * master. The rejected promise has nobody awaiting it, so it arrives here — and handed straight to
 * `unhandledRejectionHandler` it produced a red "UNCAUGHT ERROR! (in promise)!" toast reporting
 * "The user aborted a request." That was firing on every visit to Advanced Samples ▸ Grids ▸ Split
 * Master Detail, and on any grid whose filters changed quickly.
 *
 * ⚠ Matched on `name` FIRST and only then on the message, because the name is the contract —
 * DOMException sets it to "AbortError" — while the message is prose and is localised. Matching the
 * message alone would also swallow a genuine error that happened to mention aborting.
 *
 * ⚠ `preventDefault()` as well as returning, or the browser still logs it as an uncaught rejection
 * in the console and the noise this is meant to remove stays.
 */
window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: any = e?.reason;

    if (reason?.name === 'AbortError' ||
        (typeof reason?.message === 'string' && /aborted a request/i.test(reason.message))) {
        e.preventDefault();
        return;
    }

    ErrorHandling.unhandledRejectionHandler(e);
});
